import { prepareAtomicEvmRecovery } from "./onchainRecoveryEvmPublish";
import { ethers } from "ethers";
import { usesPrivateFees, type PrivateFeeQuote } from "./privateFees";
import {
  preparePrivateDepositQuote,
  authorizePrivateDeposit,
} from "./privateFeeDeposit";
import { checkPrivateFeeApproval } from "./privateFeeApproval";
import { formatInboxPublicKeyHex } from "../utils/inbox-crypto";
import { requestPrivateFees } from "../utils/relayer-client";
import { createFlatJoinSplitV2Note } from "./notes";
import {
  assertDepositProvingDeployment,
  proveDepositLocally,
} from "../deposit-wasm/prover";
import { computeCommitment, computeNPK } from "../joinsplit/keys";
import {
  NoteTokenType,
  type JoinSplitNote,
  type SpendingKeys,
  type StoredJoinSplitNote,
} from "../joinsplit/types";
import { flatTreeKey } from "./notes";
import { persistFlatJournalBackup } from "./journalBackup";
import { verifyFreshPoolDepositReceipt } from "./depositEvidence";
import {
  prepareEvmDepositMerge,
  verifyEvmDepositMergeReceipt,
  type EvmDepositMergePrepared,
} from "./depositMergeEvm";
import type { DepositProgressStep } from "./depositProgress";
import {
  assertDepositMaxMatches,
  validateDepositMaxSubmission,
  type NativeDepositMaxQuote,
} from "./depositMax";
import {
  markFlatJoinSplitV2SettlementBroadcast,
  markFlatJoinSplitV2SettlementFinalized,
  markFlatJoinSplitV2SettlementIncluded,
  markFlatJoinSplitV2SettlementWalletRejected,
  prepareFlatJoinSplitV2SettlementJournal,
  markFlatJoinSplitV2SettlementDisclosed,
  archiveFlatJoinSplitV2UndisclosedPreparation,
  type FlatJoinSplitV2JournalScope,
} from "./pendingJournal";
import {
  assertJournaledWalletSubmissionNotAborted,
  sendJournaledWalletTransaction,
  walletSettlementErrorWithContext,
} from "./walletSubmission";

const FR_MODULUS =
  21888242871839275222246405745257275088548364400416034343698204186575808495617n;
const FQ_MODULUS =
  21888242871839275222246405745257275088696311157297823662689037894645226208583n;
const MAX_DEPOSIT_AMOUNT = 1n << 120n;
const MAX_TREE_INDEX = 1n << 64n;
const MAX_LEAVES = 1n << 24n;

const DEPOSIT_POOL_ABI = [
  "function currentTreeIndex() view returns (uint256)",
  "function currentTokenHash(address token) view returns (bytes32)",
  "function computeTokenHashForTree(uint256 treeIndex,address token,uint8 tokenType,uint256 tokenSubID) view returns (bytes32)",
  "function deposit(bytes32 commitment,address token,uint256 amount,bytes32 tokenHash,bytes proof)",
  "function depositEth(bytes32 commitment,bytes32 tokenHash,bytes proof) payable",
  "event Deposit(bytes32 indexed commitment,uint256 indexed treeIndex,uint256 leafIndex,address token,uint256 amount)",
] as const;

export const FRESH_POOL_DEPOSIT_INTERFACE = new ethers.Interface(
  DEPOSIT_POOL_ABI,
);

export interface FreshPoolDepositProof {
  pi_a: [string, string];
  pi_b: [[string, string], [string, string]];
  pi_c: [string, string];
}

export interface FreshPoolDepositParams {
  signer: ethers.Signer;
  /** Chain-checked app RPC for preparation, simulation and mined evidence. */
  confirmationProvider?: ethers.Provider;
  expectedChainId: bigint | number;
  poolAddress: string;
  /** ZeroAddress selects depositEth; any other address selects ERC20 deposit. */
  tokenAddress: string;
  amount: bigint;
  tokenDecimals?: number;
  recipientMpk: bigint;
  senderMpk: bigint;
  /** Required only for an eligible self-deposit merge; ordinary deposits do not use it. */
  spendingKeys?: SpendingKeys;
  /** Current authenticated wallet note set. Used only to select a single merge input. */
  notes?: StoredJoinSplitNote[];
  journalSpendingKey: Uint8Array;
  recipientInboxPublicKeyHex?: string;
  recipientPrivateHistory?: JoinSplitNote["privateHistory"];
  signal?: AbortSignal;
  confirmations?: number;
  onProgress?: (step: DepositProgressStep) => void;
  maxQuote?: NativeDepositMaxQuote;
}

export interface FreshPoolDepositResult {
  transactionHash: string;
  blockNumber: number;
  blockHash: string;
  commitment: `0x${string}`;
  tokenHash: `0x${string}`;
  treeIndex: number;
  leafIndex: number;
  note: JoinSplitNote;
  journalId: string;
  provingTimeMs?: number;
  mode?: "deposit" | "deposit-merge";
  spentCommitments?: string[];
}

interface DepositSnapshot {
  blockNumber: number;
  blockHash: string;
  treeIndex: bigint;
  tokenHash: `0x${string}`;
}

export interface ExpectedFreshPoolDepositEvent {
  pool: string;
  commitment: string;
  token: string;
  amount: bigint;
  treeIndex: bigint;
}

export interface ParsedFreshPoolDepositEvent {
  treeIndex: number;
  leafIndex: number;
}

function fail(message: string): never {
  throw new Error(`Fresh-pool deposit rejected: ${message}`);
}

function requireAddress(value: string, label: string): string {
  try {
    return ethers.getAddress(value);
  } catch {
    return fail(`${label} is not a canonical EVM address`);
  }
}

function requireChainId(value: bigint | number): bigint {
  let chainId: bigint;
  try {
    chainId = BigInt(value);
  } catch {
    return fail("expectedChainId is invalid");
  }
  if (chainId <= 0n || chainId > BigInt(Number.MAX_SAFE_INTEGER)) {
    fail("expectedChainId must be a positive safe integer");
  }
  return chainId;
}

function requireFieldHex(
  value: unknown,
  label: string,
  nonzero = false,
): `0x${string}` {
  if (typeof value !== "string") fail(`${label} must be a bytes32 string`);
  const normalized = value.startsWith("0x") ? value : `0x${value}`;
  if (!/^0x[0-9a-fA-F]{64}$/.test(normalized)) {
    fail(`${label} must contain exactly 32 bytes`);
  }
  const parsed = BigInt(normalized);
  if (parsed >= FR_MODULUS || (nonzero && parsed === 0n)) {
    fail(`${label} is not a canonical BN254 scalar field element`);
  }
  return ethers.toBeHex(parsed, 32) as `0x${string}`;
}

function scalarHex(
  value: bigint,
  label: string,
  nonzero = false,
): `0x${string}` {
  if (value < 0n || value >= FR_MODULUS || (nonzero && value === 0n)) {
    fail(`${label} is not a canonical BN254 scalar field element`);
  }
  return ethers.toBeHex(value, 32) as `0x${string}`;
}

function proofCoordinate(value: unknown, label: string): bigint {
  if (typeof value !== "string") fail(`${label} must be a string`);
  let parsed: bigint;
  try {
    if (/^0x[0-9a-fA-F]{1,64}$/.test(value)) {
      parsed = BigInt(value);
    } else if (/^[0-9a-fA-F]{64}$/.test(value)) {
      // The Rust prover emits fixed-width, unprefixed hexadecimal. Check this
      // before decimal so an all-numeric 64-byte coordinate is not misread.
      parsed = BigInt(`0x${value}`);
    } else if (/^(0|[1-9][0-9]*)$/.test(value)) {
      parsed = BigInt(value);
    } else {
      return fail(`${label} has a malformed coordinate encoding`);
    }
  } catch {
    return fail(`${label} has a malformed coordinate encoding`);
  }
  if (parsed < 0n || parsed >= FQ_MODULUS) {
    fail(`${label} is outside the BN254 base field`);
  }
  return parsed;
}

function exactTuple(value: unknown, length: number, label: string): unknown[] {
  if (!Array.isArray(value) || value.length !== length) {
    fail(`${label} must contain exactly ${length} elements`);
  }
  return value;
}

/** Validate and ABI-encode the exact proof tuple decoded by the fresh pool. */
export function encodeFreshPoolDepositProof(proofValue: unknown): string {
  if (
    !proofValue ||
    typeof proofValue !== "object" ||
    Array.isArray(proofValue)
  ) {
    fail("deposit proof must be an object");
  }
  const proof = proofValue as Record<string, unknown>;
  const a = exactTuple(proof.pi_a, 2, "proof.pi_a");
  const b = exactTuple(proof.pi_b, 2, "proof.pi_b");
  const b0 = exactTuple(b[0], 2, "proof.pi_b[0]");
  const b1 = exactTuple(b[1], 2, "proof.pi_b[1]");
  const c = exactTuple(proof.pi_c, 2, "proof.pi_c");

  const values = [
    [
      proofCoordinate(a[0], "proof.pi_a[0]"),
      proofCoordinate(a[1], "proof.pi_a[1]"),
    ],
    [
      [
        proofCoordinate(b0[0], "proof.pi_b[0][0]"),
        proofCoordinate(b0[1], "proof.pi_b[0][1]"),
      ],
      [
        proofCoordinate(b1[0], "proof.pi_b[1][0]"),
        proofCoordinate(b1[1], "proof.pi_b[1][1]"),
      ],
    ],
    [
      proofCoordinate(c[0], "proof.pi_c[0]"),
      proofCoordinate(c[1], "proof.pi_c[1]"),
    ],
  ];

  const encoded = ethers.AbiCoder.defaultAbiCoder().encode(
    ["uint256[2]", "uint256[2][2]", "uint256[2]"],
    values,
  );
  if (!ethers.isHexString(encoded, 256)) {
    fail("encoded Groth16 deposit proof is not exactly 256 bytes");
  }
  return encoded;
}

function generateRandomField(): bigint {
  for (let attempt = 0; attempt < 128; attempt += 1) {
    const candidate = BigInt(ethers.hexlify(ethers.randomBytes(32)));
    if (candidate > 0n && candidate < FR_MODULUS) return candidate;
  }
  return fail("secure randomness did not produce a canonical field element");
}

async function assertNetwork(
  provider: ethers.Provider,
  expectedChainId: bigint,
  label = "wallet",
): Promise<void> {
  const actual = (await provider.getNetwork()).chainId;
  if (actual !== expectedChainId) {
    fail(
      `${label} chain ${String(actual)} does not match ${expectedChainId.toString()}`,
    );
  }
}

async function readDepositSnapshot(
  provider: ethers.Provider,
  pool: string,
  token: string,
): Promise<DepositSnapshot> {
  const blockNumber = await provider.getBlockNumber();
  if (!Number.isSafeInteger(blockNumber) || blockNumber < 0) {
    fail("provider returned an invalid block number");
  }
  const callAtBlock = async (name: string, args: readonly unknown[] = []) => {
    const data = FRESH_POOL_DEPOSIT_INTERFACE.encodeFunctionData(name, args);
    const raw = await provider.call({ to: pool, data, blockTag: blockNumber });
    return FRESH_POOL_DEPOSIT_INTERFACE.decodeFunctionResult(name, raw);
  };
  // Independent reads share one explicit block. The final hash barrier stays
  // after all reads so concurrency cannot hide a reorg or mix token domains.
  const [before, code, currentTreeResult, currentHashResult] =
    await Promise.all([
      provider.getBlock(blockNumber),
      provider.getCode(pool, blockNumber),
      callAtBlock("currentTreeIndex"),
      callAtBlock("currentTokenHash", [token]),
    ]);
  if (!before || before.number !== blockNumber || !before.hash) {
    fail("provider could not resolve the selected block hash");
  }
  if (!ethers.isHexString(code) || code === "0x") {
    fail("pool address has no contract code");
  }
  const treeIndex = BigInt(currentTreeResult[0]);
  if (treeIndex < 0n || treeIndex >= MAX_TREE_INDEX) {
    fail("pool currentTreeIndex is outside uint64");
  }
  const tokenHash = requireFieldHex(
    currentHashResult[0],
    "pool currentTokenHash",
    true,
  );
  const recomputedHashResult = await callAtBlock("computeTokenHashForTree", [
    treeIndex,
    token,
    0,
    0n,
  ]);
  const recomputedHash = requireFieldHex(
    recomputedHashResult[0],
    "pool tree token hash",
    true,
  );
  if (recomputedHash !== tokenHash) {
    fail("pool currentTokenHash disagrees with its tree-scoped token hash");
  }

  const after = await provider.getBlock(blockNumber);
  if (
    !after ||
    after.number !== blockNumber ||
    !after.hash ||
    after.hash.toLowerCase() !== before.hash.toLowerCase()
  ) {
    fail("selected block changed while reading the pool token domain");
  }
  return {
    blockNumber,
    blockHash: before.hash,
    treeIndex,
    tokenHash,
  };
}

async function requestDepositProof(params: {
  chainId: number;
  npk: `0x${string}`;
  tokenHash: `0x${string}`;
  amount: bigint;
  commitment: `0x${string}`;
  recoveryDigest?: string;
  signal?: AbortSignal;
  onProgress?: (step: DepositProgressStep) => void;
}): Promise<{ encodedProof: string; provingTimeMs?: number }> {
  const payload = await proveDepositLocally(
    {
      chainId: params.chainId,
      npk: params.npk,
      tokenHash: params.tokenHash,
      amount: params.amount.toString(),
      ...(params.recoveryDigest
        ? { recoveryDigest: params.recoveryDigest }
        : {}),
    },
    params.signal,
    params.onProgress,
  );
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    fail("deposit proof response must be an object");
  }
  const record = payload as unknown as Record<string, unknown>;
  if (record.success !== true)
    fail("local deposit prover did not report success");
  const returnedCommitment = requireFieldHex(
    record.commitment,
    "prover commitment",
    true,
  );
  if (returnedCommitment !== params.commitment) {
    fail("prover commitment does not match the wallet-owned note commitment");
  }
  if (
    requireFieldHex(record.token_hash, "prover token hash") !==
      params.tokenHash ||
    record.amount !== params.amount.toString()
  ) {
    fail("prover public inputs do not match the deposit");
  }
  if (
    params.recoveryDigest &&
    requireFieldHex(record.recovery_digest, "prover recovery digest", true) !==
      params.recoveryDigest.toLowerCase()
  )
    fail("prover recovery digest does not match the encrypted outputs");
  const encodedProof = encodeFreshPoolDepositProof(record.proof);
  let provingTimeMs: number | undefined;
  if (record.proving_time_ms !== undefined) {
    if (
      typeof record.proving_time_ms !== "number" ||
      !Number.isFinite(record.proving_time_ms) ||
      record.proving_time_ms < 0
    ) {
      fail("prover returned invalid timing metadata");
    }
    provingTimeMs = record.proving_time_ms;
  }
  return { encodedProof, provingTimeMs };
}

export function freshPoolTreeKey(
  chainId: bigint | number,
  poolAddress: string,
  treeIndex: bigint | number,
): string {
  const checkedChainId = requireChainId(chainId);
  let checkedTreeIndex: bigint;
  try {
    checkedTreeIndex = BigInt(treeIndex);
  } catch {
    return fail("treeIndex is invalid");
  }
  if (checkedTreeIndex < 0n || checkedTreeIndex >= MAX_TREE_INDEX) {
    fail("treeIndex must fit uint64");
  }
  return flatTreeKey(checkedChainId, poolAddress, checkedTreeIndex);
}

/** Parse exactly one canonical fresh-pool Deposit event. */
export function parseFreshPoolDepositEvent(
  logs: readonly { address: string; topics: readonly string[]; data: string }[],
  expected: ExpectedFreshPoolDepositEvent,
): ParsedFreshPoolDepositEvent {
  const pool = requireAddress(expected.pool, "expected pool");
  const topic = FRESH_POOL_DEPOSIT_INTERFACE.getEvent("Deposit")!.topicHash;
  const candidates = logs.filter(
    (log) =>
      (() => {
        try {
          return ethers.getAddress(log.address) === pool;
        } catch {
          return false;
        }
      })() && log.topics[0]?.toLowerCase() === topic.toLowerCase(),
  );
  if (candidates.length !== 1) {
    fail(
      `receipt must contain exactly one pool Deposit event, found ${candidates.length}`,
    );
  }
  let parsed: ethers.LogDescription | null;
  try {
    parsed = FRESH_POOL_DEPOSIT_INTERFACE.parseLog({
      topics: [...candidates[0].topics],
      data: candidates[0].data,
    });
  } catch {
    return fail("receipt contains a malformed Deposit event");
  }
  if (!parsed || parsed.name !== "Deposit")
    fail("receipt Deposit event could not be decoded");

  const commitment = requireFieldHex(
    parsed.args.commitment,
    "event commitment",
    true,
  );
  const expectedCommitment = requireFieldHex(
    expected.commitment,
    "expected commitment",
    true,
  );
  const token = requireAddress(parsed.args.token, "event token");
  const expectedToken = requireAddress(expected.token, "expected token");
  const treeIndex = BigInt(parsed.args.treeIndex);
  const leafIndex = BigInt(parsed.args.leafIndex);
  const amount = BigInt(parsed.args.amount);
  if (
    commitment !== expectedCommitment ||
    token !== expectedToken ||
    treeIndex !== expected.treeIndex ||
    amount !== expected.amount
  ) {
    fail("Deposit event does not match the simulated transaction");
  }
  if (treeIndex < 0n || treeIndex > BigInt(Number.MAX_SAFE_INTEGER)) {
    fail(
      "event treeIndex cannot be represented safely by the wallet note model",
    );
  }
  if (leafIndex < 0n || leafIndex >= MAX_LEAVES) {
    fail("event leafIndex is outside the depth-24 tree");
  }
  return { treeIndex: Number(treeIndex), leafIndex: Number(leafIndex) };
}

/** A one-wei, proof-valid dry run, independent of all wallet notes and keys.
 * This payload is used only by eth_estimateGas, never by a signer or journal.
 * Native deposit gas executes the same verification/tree insertion for any
 * positive amount. Random proof bytes and changing tree state are covered by
 * the quote's gas margin and an exact-transaction check before wallet signing.
 */
export async function createNativeDepositSimulation(params: {
  provider: ethers.Provider;
  account: string;
  pool: string;
  chainId: number;
  signal?: AbortSignal;
}) {
  const chainId = requireChainId(params.chainId);
  const pool = requireAddress(params.pool, "pool");
  const account = requireAddress(params.account, "account");
  assertDepositProvingDeployment(Number(chainId), pool);
  await assertNetwork(params.provider, chainId, "estimate RPC");
  const snapshot = await readDepositSnapshot(
    params.provider,
    pool,
    ethers.ZeroAddress,
  );
  if (usesPrivateFees(pool)) {
    // A real, unsent three-output deposit exercises the fee-bearing path. The
    // tiny value is only eth_call/estimateGas input; it is never broadcast.
    const amount = 10000n;
    const quote = await preparePrivateDepositQuote({
      amount,
      pool,
      chainId: Number(chainId),
      treeIndex: Number(snapshot.treeIndex),
      tokenHash: snapshot.tokenHash,
      signal: params.signal,
    });
    const note = await createFlatJoinSplitV2Note({
      recipientMpk: 1n,
      value: BigInt(quote.amounts.recipientAmount),
      chainId,
      poolAddress: pool,
      treeIndex: snapshot.treeIndex,
      tokenAddress: ethers.ZeroAddress,
      tokenHash: snapshot.tokenHash,
    });
    const inboxPublicKey = formatInboxPublicKeyHex(ethers.randomBytes(32));
    const recovery = await prepareAtomicEvmRecovery({
      provider: params.provider,
      chainId: Number(chainId),
      pool,
      outputs: [{ note, inboxPublicKey }],
      feeOutputs: quote.feeOutputs,
      signal: params.signal,
    });
    const prove = async (n: JoinSplitNote) =>
      (
        await requestDepositProof({
          chainId: Number(chainId),
          npk: scalarHex(n.npk, "simulation NPK", true),
          recoveryDigest: recovery?.digest,
          tokenHash: snapshot.tokenHash,
          amount: n.value,
          commitment: n.commitment as `0x${string}`,
          signal: params.signal,
        })
      ).encodedProof;
    const data = await authorizePrivateDeposit({
      quote,
      note,
      recipientMpk: 1n,
      inboxPublicKey,
      recovery,
      proof: await prove(note),
      prove,
      signal: params.signal,
    });
    return { from: account, to: pool, chainId, value: amount, data };
  }
  const amount = 1n;
  const note = await createFlatJoinSplitV2Note({
    recipientMpk: 1n,
    value: amount,
    chainId,
    poolAddress: pool,
    treeIndex: snapshot.treeIndex,
    tokenAddress: ethers.ZeroAddress,
    tokenHash: snapshot.tokenHash,
  });
  const npk = note.npk,
    commitment = note.commitment as `0x${string}`;
  const recovery = await prepareAtomicEvmRecovery({
    provider: params.provider,
    chainId: Number(chainId),
    pool,
    outputs: [
      { note, inboxPublicKey: formatInboxPublicKeyHex(ethers.randomBytes(32)) },
    ],
    signal: params.signal,
  });
  const { encodedProof } = await requestDepositProof({
    chainId: Number(chainId),
    npk: scalarHex(npk, "simulation NPK", true),
    recoveryDigest: recovery?.digest,
    tokenHash: snapshot.tokenHash,
    amount,
    commitment,
    signal: params.signal,
  });
  if (params.signal?.aborted)
    throw new DOMException("Estimate cancelled", "AbortError");
  const data = FRESH_POOL_DEPOSIT_INTERFACE.encodeFunctionData("depositEth", [
    commitment,
    snapshot.tokenHash,
    encodedProof,
  ]);
  return {
    from: account,
    to: pool,
    chainId,
    value: amount,
    data: recovery ? recovery.wrap(data) : data,
  };
}

/**
 * Create and settle a fungible deposit into the fresh depth-24 pool.
 *
 * Note randomness and the NPK witness stay in the browser. A dedicated WASM
 * worker proves with pinned keys and verifies locally. ERC20 allowance must
 * already be in place; a failed simulation prevents transaction submission.
 */
export async function executeFreshPoolDeposit(
  params: FreshPoolDepositParams,
): Promise<FreshPoolDepositResult> {
  const progress = (step: DepositProgressStep) => {
    try {
      params.onProgress?.(step);
    } catch {
      // UI/logging callbacks must never change transaction or recovery behavior.
    }
  };
  progress("checking");
  if (!params.signer) fail("signer is required");
  const provider = params.signer.provider;
  if (!provider) fail("signer must have a provider");
  const confirmationProvider = params.confirmationProvider ?? provider;
  const chainId = requireChainId(params.expectedChainId);
  const pool = requireAddress(params.poolAddress, "poolAddress");
  const token = requireAddress(params.tokenAddress, "tokenAddress");
  if (params.amount <= 0n || params.amount >= MAX_DEPOSIT_AMOUNT) {
    fail("amount must be positive and fit uint120");
  }
  scalarHex(params.recipientMpk, "recipientMpk", true);
  if (
    params.confirmations !== undefined &&
    (!Number.isSafeInteger(params.confirmations) || params.confirmations < 1)
  ) {
    fail("confirmations must be a positive safe integer");
  }
  assertDepositProvingDeployment(Number(chainId), pool);
  if (params.signal?.aborted)
    throw new DOMException("Deposit proving cancelled", "AbortError");
  await assertNetwork(provider, chainId);
  if (confirmationProvider !== provider) {
    await assertNetwork(confirmationProvider, chainId, "confirmation RPC");
  }
  const signerAddress = requireAddress(
    await params.signer.getAddress(),
    "signer address",
  );
  if (params.maxQuote) {
    if (token !== ethers.ZeroAddress)
      fail("ETH MAX estimate cannot be used for another token");
    assertDepositMaxMatches(params.maxQuote, {
      from: signerAddress,
      to: pool,
      chainId,
      value: params.amount,
    });
  }
  const initialSnapshot = await readDepositSnapshot(
    confirmationProvider,
    pool,
    token,
  );
  const privateFeePool = usesPrivateFees(pool);
  let privateFeeQuote: PrivateFeeQuote | null = null;
  let receivedAmount = params.amount;

  const isEth = token === ethers.ZeroAddress;
  let mergePrepared: EvmDepositMergePrepared | null = null;
  if (
    params.spendingKeys &&
    params.notes &&
    params.senderMpk === params.recipientMpk
  ) {
    mergePrepared = await prepareEvmDepositMerge({
      provider: confirmationProvider,
      chainId,
      pool,
      token,
      payer: signerAddress,
      netCredit: receivedAmount,
      senderMpk: params.senderMpk,
      recipientMpk: params.recipientMpk,
      spendingKeys: params.spendingKeys,
      notes: params.notes,
      tokenDecimals: params.tokenDecimals,
      privateHistory: params.recipientPrivateHistory,
      signal: params.signal,
      onProgress: (phase) => progress(phase),
      privateFees: privateFeePool
        ? {
            grossAmount: params.amount,
            inboxPublicKey:
              params.recipientInboxPublicKeyHex ??
              formatInboxPublicKeyHex(params.journalSpendingKey),
            proveFee: async (note, recoveryDigest) =>
              (
                await requestDepositProof({
                  chainId: Number(chainId),
                  recoveryDigest,
                  npk: scalarHex(note.npk, "fee NPK", true),
                  tokenHash: initialSnapshot.tokenHash,
                  amount: note.value,
                  commitment: note.commitment as `0x${string}`,
                  signal: params.signal,
                  onProgress: progress,
                })
              ).encodedProof,
          }
        : undefined,
    });
  }

  let pendingNote: JoinSplitNote;
  let commitment: `0x${string}`;
  let data: string;
  let provingTimeMs: number | undefined;

  if (mergePrepared) {
    privateFeeQuote = mergePrepared.feeQuote ?? null;
    receivedAmount = mergePrepared.netCredit;
    pendingNote = mergePrepared.note;
    commitment = requireFieldHex(
      mergePrepared.note.commitment,
      "merged wallet commitment",
      true,
    );
    data = mergePrepared.transactionData;
    provingTimeMs = mergePrepared.provingTimeMs;
  } else {
    if (privateFeePool) {
      privateFeeQuote = await preparePrivateDepositQuote({
        amount: params.amount,
        pool,
        chainId: Number(chainId),
        treeIndex: Number(initialSnapshot.treeIndex),
        tokenHash: initialSnapshot.tokenHash,
        token,
        signal: params.signal,
      });
      checkPrivateFeeApproval(privateFeeQuote, "deposit");
      receivedAmount = BigInt(privateFeeQuote.amounts.recipientAmount);
    }
    const random = generateRandomField();
    const npk = await computeNPK(params.recipientMpk, random);
    const npkHex = scalarHex(npk, "note public key", true);
    const tokenHashValue = BigInt(initialSnapshot.tokenHash);
    commitment = requireFieldHex(
      await computeCommitment(npk, tokenHashValue, receivedAmount),
      "wallet commitment",
      true,
    );
    pendingNote = {
      schemaVersion: 2,
      protocol: "flat-joinsplit-v2",
      chainId: Number(chainId),
      poolAddress: pool,
      npk,
      random,
      commitment,
      tokenHash: tokenHashValue,
      tokenData: {
        tokenAddress: token,
        tokenType: NoteTokenType.ERC20,
        tokenSubID: "0",
      },
      value: receivedAmount,
      leafIndex: -1,
      treeIndex: Number(initialSnapshot.treeIndex),
      treeKey: freshPoolTreeKey(chainId, pool, initialSnapshot.treeIndex),
      privateHistory: params.recipientPrivateHistory
        ? {
            ...params.recipientPrivateHistory,
            amount: ethers.formatUnits(
              receivedAmount,
              params.tokenDecimals ?? 18,
            ),
          }
        : undefined,
    };
    const recoveryInboxKey =
      params.recipientInboxPublicKeyHex ||
      (params.senderMpk === params.recipientMpk
        ? formatInboxPublicKeyHex(params.journalSpendingKey)
        : undefined);
    if (!recoveryInboxKey) fail("Recipient recovery information is missing");
    const recovery = await prepareAtomicEvmRecovery({
      provider: confirmationProvider,
      chainId: Number(chainId),
      pool,
      outputs: [{ note: pendingNote, inboxPublicKey: recoveryInboxKey }],
      feeOutputs: privateFeeQuote?.feeOutputs,
      signal: params.signal,
    });
    progress("loading");
    const ordinaryProof = await requestDepositProof({
      chainId: Number(chainId),
      npk: npkHex,
      recoveryDigest: recovery?.digest,
      tokenHash: initialSnapshot.tokenHash,
      amount: receivedAmount,
      commitment,
      signal: params.signal,
      onProgress: progress,
    });
    provingTimeMs = ordinaryProof.provingTimeMs;
    data = isEth
      ? FRESH_POOL_DEPOSIT_INTERFACE.encodeFunctionData("depositEth", [
          commitment,
          initialSnapshot.tokenHash,
          ordinaryProof.encodedProof,
        ])
      : FRESH_POOL_DEPOSIT_INTERFACE.encodeFunctionData("deposit", [
          commitment,
          token,
          params.amount,
          initialSnapshot.tokenHash,
          ordinaryProof.encodedProof,
        ]);
    if (privateFeeQuote) {
      const inboxPublicKey =
        params.recipientInboxPublicKeyHex ||
        (params.senderMpk === params.recipientMpk
          ? formatInboxPublicKeyHex(params.journalSpendingKey)
          : undefined);
      if (!inboxPublicKey) fail("Recipient recovery information is missing");
      data = await authorizePrivateDeposit({
        quote: privateFeeQuote,
        recovery,
        note: pendingNote,
        recipientMpk: params.recipientMpk,
        inboxPublicKey,
        proof: ordinaryProof.encodedProof,
        signal: params.signal,
        prove: async (note) =>
          (
            await requestDepositProof({
              chainId: Number(chainId),
              npk: scalarHex(note.npk, "fee NPK", true),
              recoveryDigest: recovery?.digest,
              tokenHash: initialSnapshot.tokenHash,
              amount: note.value,
              commitment: note.commitment as `0x${string}`,
              signal: params.signal,
              onProgress: progress,
            })
          ).encodedProof,
      });
    } else if (recovery) data = recovery.wrap(data);
  }
  if (params.signal?.aborted) fail("deposit was cancelled before simulation");

  // Proof generation can be slow. Re-read the tree-scoped asset domain and
  // fail locally if rollover happened while the proof was being generated.
  progress("checking");
  await assertNetwork(provider, chainId);
  const finalSnapshot = await readDepositSnapshot(
    confirmationProvider,
    pool,
    token,
  );
  if (
    finalSnapshot.treeIndex !== initialSnapshot.treeIndex ||
    finalSnapshot.tokenHash !== initialSnapshot.tokenHash
  ) {
    fail("pool tree/token domain changed while generating the deposit proof");
  }
  if (
    mergePrepared &&
    mergePrepared.tokenHash.toLowerCase() !==
      initialSnapshot.tokenHash.toLowerCase()
  ) {
    fail("deposit merge token domain changed during preparation");
  }
  const transaction = Object.freeze({
    to: pool,
    from: signerAddress,
    data,
    value: isEth ? params.amount : 0n,
    chainId,
  });

  // eth_call executes the exact to/data/value/from tuple that will be passed
  // to the signer, catching stale domains, bad proofs, missing allowance, and
  // pool policy failures before any transaction is submitted.
  await assertNetwork(provider, chainId);
  if (
    requireAddress(
      await params.signer.getAddress(),
      "current signer address",
    ) !== signerAddress
  ) {
    fail("wallet account changed before deposit signature");
  }
  if (params.signal?.aborted) fail("deposit was cancelled before submission");
  const journalScope: FlatJoinSplitV2JournalScope = {
    chainId,
    poolAddress: pool,
    accountAddress: signerAddress,
    spendingKey: params.journalSpendingKey,
  };
  progress("backup");
  const journal = mergePrepared?.journalId
    ? { id: mergePrepared.journalId }
    : await prepareFlatJoinSplitV2SettlementJournal(journalScope, {
        kind: "deposit",
        ...(privateFeeQuote ? {} : { proofDisclosure: "undisclosed" as const }),
        transaction: {
          to: pool,
          from: signerAddress,
          data,
          value: transaction.value.toString(),
          chainId: Number(chainId),
        },
        sourceCommitments: mergePrepared
          ? [mergePrepared.input.note.commitment]
          : undefined,
        sourceNullifiers: mergePrepared
          ? [mergePrepared.sourceNullifier]
          : undefined,
        outputNotes: [pendingNote],
        deliveries: [
          {
            commitment,
            recipientMpkHex: scalarHex(
              params.recipientMpk,
              "journal recipient MPK",
              true,
            ),
            recipientInboxPublicKeyHex: params.recipientInboxPublicKeyHex,
            keepLocal: params.recipientMpk === params.senderMpk,
          },
        ],
      });
  const walletSubmission = {
    journal: { id: journal.id, scope: journalScope },
    signal: params.signal,
  };
  let submittedHash: string | undefined;
  try {
    await assertJournaledWalletSubmissionNotAborted(walletSubmission);
    if (!mergePrepared?.journalId)
      await persistFlatJournalBackup(journalScope, journal.id, {
        signal: params.signal,
      });
    await assertJournaledWalletSubmissionNotAborted(walletSubmission);
    // Only disclose the complete call after its encrypted output secrets exist
    // outside this browser. The acknowledgement is content-bound and fail-closed.
    progress("simulation");
    await markFlatJoinSplitV2SettlementDisclosed(journalScope, journal.id);
    if (params.signal?.aborted) fail("deposit was cancelled before simulation");
    await confirmationProvider.call(transaction);
    let submissionTransaction: ethers.TransactionRequest = transaction;
    if (params.maxQuote) {
      try {
        submissionTransaction = await validateDepositMaxSubmission(
          confirmationProvider,
          transaction,
          params.maxQuote,
        );
      } catch (error) {
        // The wallet has not been invoked. Archive only this unsubmitted intent;
        // keep its encrypted recovery secrets if storage itself fails.
        await markFlatJoinSplitV2SettlementWalletRejected(
          journalScope,
          journal.id,
        ).catch(() => {});
        throw error;
      }
    }
    await assertNetwork(provider, chainId);
    if (
      requireAddress(
        await params.signer.getAddress(),
        "current signer address",
      ) !== signerAddress
    ) {
      fail("wallet account changed after deposit journal persistence");
    }
    progress("screening");
    const response = await sendJournaledWalletTransaction({
      ...walletSubmission,
      signer: params.signer,
      transaction: submissionTransaction,
      onWalletRequest: () => progress("wallet"),
    });
    if (response && /^0x[0-9a-fA-F]{64}$/.test(response.hash)) {
      submittedHash = response.hash;
    }
    if (!response || !/^0x[0-9a-fA-F]{64}$/.test(response.hash)) {
      fail(
        "wallet returned an invalid transaction hash; deposit recovery remains pending",
      );
    }

    try {
      await markFlatJoinSplitV2SettlementBroadcast(
        journalScope,
        journal.id,
        response.hash,
      );
    } catch {
      // The prepared record still contains the exact intent and output secret;
      // startup reconciliation can discover a copied or submitted transaction.
    }

    // Some wallet RPCs omit chainId on the initial transaction response. Keep
    // the broadcast hash, and require complete chain-bound mined evidence below.
    // A supplied conflicting chain is still rejected rather than filled in.
    if (
      response.chainId != null &&
      requireChainId(response.chainId) !== chainId
    ) {
      fail(
        "wallet returned a conflicting transaction chain; deposit recovery remains pending",
      );
    }

    // Smart wallets may return a bundler's outer transaction. Preserve its hash
    // and verify the actual pool outcome below, before crediting any note.
    progress("confirming");
    let includedReceipt: ethers.TransactionReceipt | null;
    let receiptHash = response.hash;
    try {
      includedReceipt = await response.wait(1);
    } catch (error: any) {
      if (
        error?.code === "TRANSACTION_REPLACED" &&
        error?.cancelled === false &&
        error?.receipt
      ) {
        includedReceipt = error.receipt as ethers.TransactionReceipt;
        receiptHash = includedReceipt.hash;
      } else {
        throw error;
      }
    }
    if (
      !includedReceipt ||
      includedReceipt.status !== 1 ||
      includedReceipt.hash.toLowerCase() !== receiptHash.toLowerCase()
    ) {
      fail("deposit transaction reverted");
    }
    try {
      await markFlatJoinSplitV2SettlementIncluded(journalScope, journal.id, {
        txHash: includedReceipt.hash,
        blockNumber: includedReceipt.blockNumber,
        blockHash: includedReceipt.blockHash,
      });
    } catch {
      // Preserve the earlier durable journal state and reconcile from chain.
    }
    const confirmations = params.confirmations ?? 1;
    const receipt =
      confirmations <= 1
        ? includedReceipt
        : await confirmationProvider.waitForTransaction(
            includedReceipt.hash,
            confirmations,
          );
    if (!receipt || receipt.hash.toLowerCase() !== receiptHash.toLowerCase()) {
      fail("deposit transaction receipt is missing or inconsistent");
    }
    progress("verifying");
    const event = mergePrepared
      ? await verifyEvmDepositMergeReceipt({
          provider: confirmationProvider,
          receipt,
          chainId,
          pool,
          note: pendingNote,
          sourceNullifier: mergePrepared.sourceNullifier,
          netCredit: receivedAmount,
        })
      : await verifyFreshPoolDepositReceipt({
          provider: confirmationProvider,
          receipt,
          chainId,
          pool,
          note: pendingNote,
        });

    const note: JoinSplitNote = {
      ...pendingNote,
      leafIndex: event.leafIndex,
      treeIndex: event.treeIndex,
      treeKey: freshPoolTreeKey(chainId, pool, event.treeIndex),
    };
    if (privateFeeQuote) {
      // Confirmation is repeatable and derived from public chain evidence. A
      // temporary acknowledgement failure cannot undo a successful deposit.
      void requestPrivateFees(
        "confirm",
        { id: privateFeeQuote.id, transactionHash: receipt.hash },
        { pool },
      ).catch(() => {});
    }

    try {
      await markFlatJoinSplitV2SettlementFinalized(journalScope, journal.id, {
        txHash: receipt.hash,
        blockNumber: receipt.blockNumber,
        blockHash: receipt.blockHash,
        outputNotes: [note],
      });
    } catch {
      // The encrypted prepared/included record remains recoverable.
    }

    return Object.freeze({
      transactionHash: receipt.hash,
      blockNumber: receipt.blockNumber,
      blockHash: receipt.blockHash,
      commitment,
      tokenHash: initialSnapshot.tokenHash,
      treeIndex: event.treeIndex,
      leafIndex: event.leafIndex,
      note: Object.freeze(note),
      journalId: journal.id,
      provingTimeMs,
      mode: mergePrepared ? "deposit-merge" : "deposit",
      spentCommitments: mergePrepared
        ? [mergePrepared.input.note.commitment]
        : undefined,
    });
  } catch (error) {
    await archiveFlatJoinSplitV2UndisclosedPreparation(
      journalScope,
      journal.id,
    ).catch(() => {});
    throw walletSettlementErrorWithContext(error, journal.id, submittedHash);
  }
}
